/* eslint-disable no-nested-ternary */
import { useEffect, useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess } from 'Deer/helpers/getUserAccess';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { INSIGHT_VIEWS, useOKRInsights } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import {
  checkBulkUploadIntentEnabled,
  checkVisierAnalyticsEnabled,
  checkBulkCheckInEnabled,
  checkGoalDataExportsReportEnabled,
} from 'utils/helpers/featureFlags';
import { checkGoalModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsCustomGoalAdmin, checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { GOAL_INTENTS } from 'constants/moduleIntentTypes';
import { isNetchexLogin } from 'constants/netChexConstants';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useOKREllipsisValues } from '../hooks/useOKREllipsisValues';
import { useOKRGroupByValues } from '../hooks/useOKRGroupByValues';
import { useOKRViewTypeValues } from '../hooks/useOKRViewTypeValues';
import { GoalsIntentType, setSelectedGoalsIntent } from '../slice/GoalsIntentSlice';
import { MetaFilters } from './MetaFilters';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IntentDropdown = ({
  gridRef,
  showKebabMenu = true,
  timelineDropdownOnChangeCallback,
  ownershipDropdownOnChangeCallback,
  breadcrumbs,
  disableMetaFilters = false,
  disableEllipsisMenu = false,
}: {
  gridRef?: unknown;
  showKebabMenu?: boolean;
  disableMetaFilters?: boolean;
  disableEllipsisMenu?: boolean;
  timelineDropdownOnChangeCallback?: (onChange: () => void) => void;
  ownershipDropdownOnChangeCallback?: (onChange: () => void) => void;
  breadcrumbs: IntentBreadcrumbType;
}) => {
  const { moduleName } = useOrganisationSettings();
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const applicableGroupByValues = useOKRGroupByValues();
  const sideNavSelectedCohort = useSelectedView();
  const selectedCohortId = sideNavSelectedCohort?.id;
  const { viewType: OKRManageView } = useOKRManageViewType();
  const userAccess = getUserAccess();
  const { viewType: selectedGroupBy } = useOKRInsights();
  const groupByDropdownAllowed = selectedCohortId !== -12 && OKRManageView === 'list';

  const applicableOKRViewValues = useOKRViewTypeValues();

  const {
    canCreateObjectiveAtRootLevel,

    canCreateObjectiveUnderKeyResult,
    canCreateObjectiveUnderObjective,
    canCreateKeyResultUnderKeyResult,
    canCreateKeyResultUnderObjective,
    canCreateKeyResultAtRootLevel,
  } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateObjective =
    canCreateObjectiveAtRootLevel || canCreateObjectiveUnderObjective || canCreateObjectiveUnderKeyResult;

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<GoalsIntentType>>(() => {
    return [
      {
        id: GOAL_INTENTS.MANAGE_GOALS,
        label: `${l1Module === 'myData' ? 'My' : l1Module === 'team' ? 'Team' : 'Company'} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].goal.myGoals,
            team: {
              directReports: L3_PATHS.team.directReports.goal.manage,
              allReports: L3_PATHS.team.allReports.goal.manage,
              view: L3_PATHS.team.view.goal.manage,
            },
            company: L3_PATHS.company.goal.manage,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];

          // TODO @SHREEYANSH : Add manageViewType to query params
          // const queryParams = new URLSearchParams(search);
          // queryParams.set('manageViewType', OKRManageView);

          history.push({
            pathname: urlToPush,
            search: search.includes('viewId') ? search : '',
          });
        },
        visible: () => true,
        value: {
          myData: {
            path: L3_PATHS['my-data'].goal.myGoals,
            isAccessible: checkGoalModuleEnabled,
          },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.goal.manage,
              isAccessible: checkGoalModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.goal.manage,
              isAccessible: checkGoalModuleEnabled,
            },
            view: {
              path: L3_PATHS.team.view.goal.manage,
              isAccessible: checkGoalModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.goal.manage,
            isAccessible: () => checkUserIsGoalAdmin() || userAccess?.isCustomGoalAdmin,
          },
        },
        applicableGroupByValues,
        showGroupByDropdown: groupByDropdownAllowed,
        applicableOKRViewValues,
        showOKRViewDropdown: true,
        showTimelineDropdown: true,
        showGoalOwnershipFilter: !groupByDropdownAllowed || selectedGroupBy !== INSIGHT_VIEWS[0].value,
        category: 'Manage',
        isDefault: true,
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.COMPANY_GOALS,
        label: `Company ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].goal.myCompany,
          };
          const urlToPush = values.myData;
          history.push(urlToPush);
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].goal.myCompany,
            isAccessible: checkGoalModuleEnabled,
          },
        },
        applicableGroupByValues,
        showGroupByDropdown: groupByDropdownAllowed,
        applicableOKRViewValues,
        showOKRViewDropdown: true,
        showTimelineDropdown: true,
        showGoalOwnershipFilter: !groupByDropdownAllowed || selectedGroupBy !== INSIGHT_VIEWS[0].value,
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.ARCHIVED_GOALS,
        label: `Archived ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => {
          history.push(L3_PATHS.company.goal.archived);
        },
        visible: () => (checkUserIsGoalAdmin() || userAccess?.isCustomGoalAdmin) && l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.goal.archived,
            isAccessible: checkUserIsGoalAdmin,
          },
        },
        showGroupByDropdown: false,
        showOKRViewDropdown: false,
        showTimelineDropdown: true,
        showGoalOwnershipFilter: false,
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.UPLOAD_GOALS,
        label: `Import ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].goal.upload,
            team: {
              directReports: L3_PATHS.team.directReports.goal.upload,
              allReports: L3_PATHS.team.allReports.goal.upload,
              view: L3_PATHS.team.view.goal.upload,
            },
            company: L3_PATHS.company.goal.upload,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];
          history.push({
            pathname: urlToPush,
            search: search.includes('viewId') ? search : '',
          });
        },
        visible: () => checkBulkUploadIntentEnabled() && (canCreateObjective || canCreateKeyResult),
        value: {
          myData: { path: L3_PATHS['my-data'].goal.upload, isAccessible: checkBulkUploadIntentEnabled },
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.goal.upload,
              isAccessible: checkBulkUploadIntentEnabled,
            },
            allReports: { path: L3_PATHS.team.allReports.goal.upload, isAccessible: checkBulkUploadIntentEnabled },
            view: {
              path: L3_PATHS.team.view.goal.upload,
              isAccessible: checkBulkUploadIntentEnabled,
            },
          },
          company: { path: L3_PATHS.company.goal.upload, isAccessible: checkBulkUploadIntentEnabled },
        },
        showGroupByDropdown: false,
        showTimelineDropdown: true,
        timelineDropdownOnChangeCallback,
        category: 'Create',
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.GOAL_REPORTS,
        label: `Reports`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.goal.embeddedAnalytics,

              allReports: L3_PATHS.team.allReports.goal.embeddedAnalytics,
            },
            company: L3_PATHS.company.goal.embeddedAnalytics,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () =>
          checkVisierAnalyticsEnabled() &&
          (l1Module === 'company' || (l1Module === 'team' && teamType !== 'view')) &&
          checkUserIsGoalAdmin(),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.goal.embeddedAnalytics,
              isAccessible: checkVisierAnalyticsEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.goal.embeddedAnalytics,
              isAccessible: checkVisierAnalyticsEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.goal.embeddedAnalytics,
            isAccessible: checkVisierAnalyticsEnabled,
          },
        },
        showGroupByDropdown: false,
        showOKRViewDropdown: false,
        showTimelineDropdown: false,
        category: 'Analyze',
        hasMetaFilters: false,
      },
      {
        id: GOAL_INTENTS.DATA_EXPORTS,
        label: `Data exports`,
        onClick: () => {
          const values = {
            company: L3_PATHS.company.goal.dataExports,
          };
          const urlToPush = values.company;
          history.push(urlToPush);
        },
        visible: () =>
          l1Module === 'company' &&
          (checkUserIsGoalAdmin() || checkUserIsCustomGoalAdmin()) &&
          checkGoalDataExportsReportEnabled(),
        value: {
          company: {
            path: L3_PATHS.company.goal.dataExports,
            isAccessible: () =>
              (checkUserIsGoalAdmin() || checkUserIsCustomGoalAdmin()) && checkGoalDataExportsReportEnabled(),
          },
        },

        showTimelineDropdown: true,
        category: 'Analyze',
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.CHECK_IN_GOALS,
        label: `Check-in to multiple ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        onClick: () => {
          const values = {
            myData: L3_PATHS['my-data'].goal.checkIn,
            team: {
              directReports: L3_PATHS.team.directReports.goal.checkIn,
              allReports: L3_PATHS.team.allReports.goal.checkIn,
              view: L3_PATHS.team.view.goal.checkIn,
            },
            company: L3_PATHS.company.goal.checkIn,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : values[l1Module];

          history.push({
            pathname: urlToPush,
            search: search.includes('viewId') ? search : '',
          });
        },
        visible: () => checkBulkCheckInEnabled(),
        value: {
          myData: { path: L3_PATHS['my-data'].goal.checkIn, isAccessible: checkBulkCheckInEnabled },
          team: {
            directReports: { path: L3_PATHS.team.directReports.goal.checkIn, isAccessible: checkBulkCheckInEnabled },
            allReports: { path: L3_PATHS.team.allReports.goal.checkIn, isAccessible: checkBulkCheckInEnabled },
            view: {
              path: L3_PATHS.team.view.goal.checkIn,
              isAccessible: checkBulkCheckInEnabled,
            },
          },
          company: { path: L3_PATHS.company.goal.checkIn, isAccessible: checkBulkCheckInEnabled },
        },
        applicableGroupByValues,
        showGroupByDropdown: false,
        applicableOKRViewValues,
        showOKRViewDropdown: true,
        showTimelineDropdown: true,
        showGoalOwnershipFilter: !groupByDropdownAllowed || selectedGroupBy !== INSIGHT_VIEWS[0].value,
        timelineDropdownOnChangeCallback,
        ownershipDropdownOnChangeCallback,
        category: 'Check-in',
        hasMetaFilters: true,
      },
      {
        id: GOAL_INTENTS.GOAL_SETTINGS,
        label: `Settings`,
        onClick: () => {
          history.push(L3_PATHS.company.goal.settings);
        },
        visible: () => {
          if (isNetchexLogin) {
            return (checkUserIsGoalAdmin() || userAccess?.isCustomGoalAdmin) && l1Module === 'company';
          }
          return checkUserIsGoalAdmin() && l1Module === 'company';
        },
        value: {
          company: {
            path: L3_PATHS.company.goal.settings,
            isAccessible: () => {
              if (isNetchexLogin) {
                return checkUserIsGoalAdmin() || userAccess?.isCustomGoalAdmin;
              }
              return checkUserIsGoalAdmin();
            },
          },
        },
        showGroupByDropdown: false,
        showOKRViewDropdown: false,
        showTimelineDropdown: false,
        showGoalOwnershipFilter: false,
        category: 'Configure',
        hasMetaFilters: false,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moduleName,
    applicableGroupByValues,
    selectedCohortId,
    OKRManageView,
    applicableOKRViewValues,
    l1Module,
    teamType,
    history,
    userAccess?.isCustomGoalAdmin,
  ]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        const isTeamType = l1Module === 'team';
        if (isTeamType) {
          return pathname.includes(option.value.team?.[teamType]?.path);
        }
        return pathname.includes(option.value[l1Module]?.path);
      }),
    [intentDropdownOptions, pathname, l1Module, teamType]
  );

  const ellipsisMenuOptions = useOKREllipsisValues({
    gridRef,
    selectedIntent,
  });

  useEffect(() => {
    // if selectedIntent changes then update it in redux
    dispatch(setSelectedGoalsIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={
        // @ts-expect-error options type error
        <AgGridEllipsisMenu
          options={ellipsisMenuOptions}
          selectedRadioValue="showArchivedGoals"
          disableEllipsisMenu={disableEllipsisMenu}
        />
      }
      MetaFilters={<MetaFilters disableMetaFilters={disableMetaFilters} />}
      showKebabMenu={showKebabMenu}
      intentTitle={modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}
      moduleName={meshModules.goals.value}
      isCategorized
      breadcrumbs={breadcrumbs}
      gridRef={gridRef}
      ellipsisMenuOptions={ellipsisMenuOptions}
    />
  );
};

export { IntentDropdown };
